import { type FC, type ReactNode, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { type ActionItem, SpinnerLoader, Typography } from 'shared/ui'
import { Table, type ITableProps, TableSearch } from 'shared/ui/TableNew'
import { inboxCell, phoneCell, statusCell, typeCell } from 'entities/Phone/ui/Table/columns'
import { type NumbersListStore } from 'entities/Phone'
import { type RowNumber } from 'entities/Phone/model/Table/RowNumber'
import styles from './styles.module.scss'

type INumbersTableProps = {
  numbersListStore: NumbersListStore
  hiddenColumns?: string[]
  notFoundMessage?: string
  getIsRowDisabled: (row: RowNumber) => boolean
  getIsRowCheckboxDisabled?: (row: RowNumber) => boolean
  getRowActions: (row: RowNumber) => ActionItem[]
  emptyContent?: ReactNode
} & Omit<ITableProps<RowNumber>, 'columns' | 'rows' | 'store'>

export const NumbersTable: FC<INumbersTableProps> = observer(
  ({
    numbersListStore,
    hiddenColumns,
    notFoundMessage,
    getIsRowDisabled,
    getIsRowCheckboxDisabled,
    getRowActions,
    emptyContent,
    ...tableProps
  }) => {
    const { loading, firstLoading, tableStore, scrollToTOpTrigger, search, setSearch, itemsRows } =
      numbersListStore

    const rows = useMemo(() => {
      return itemsRows.map((item) => {
        item.setActionsProps({
          actions: getRowActions(item),
        })
        item.setDisabled(getIsRowDisabled(item))
        item.setAriaLabel(getIsRowDisabled(item) ? 'disabled' : '')
        if (getIsRowCheckboxDisabled) {
          item.setCheckboxDisabled(getIsRowCheckboxDisabled(item))
        }

        return item
      })
    }, [itemsRows])

    const columns = [phoneCell, inboxCell, statusCell, typeCell].filter(
      (column) => !hiddenColumns?.includes(column.field)
    )

    const getContent = () => {
      if (firstLoading) {
        return <SpinnerLoader ariaLabel={'NumbersTable'} />
      }

      if (!rows.length && !search && emptyContent && !loading) {
        return emptyContent
      }

      const searchField = <TableSearch value={search} onChange={setSearch} />

      if (!tableStore.loading && !rows.length) {
        return (
          <>
            {searchField}
            <div className={styles.table}>
              <div className={styles.emptySearch}>
                <Typography
                  variant={'body-md-regular'}
                  ellipsis
                  ariaLabel={'empty_placeholder'}
                  color={'tertiary'}
                >
                  {notFoundMessage}
                </Typography>
              </div>
            </div>
          </>
        )
      }

      return (
        <>
          {searchField}
          <div className={styles.table}>
            <Table
              store={tableStore}
              columns={columns}
              rows={rows}
              scrollToTOpTrigger={scrollToTOpTrigger}
              virtualization
              {...tableProps}
            />
          </div>
        </>
      )
    }

    return <div className={styles.root}>{getContent()}</div>
  }
)
